import { useState, useRef, useEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  Home, History, FileText, Briefcase, User, Award, LogOut, ChevronDown, ChevronUp,
  Sparkles, Search, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { cn } from '../lib/utils'
import { authAPI } from '../services/api'
import { useToast } from '../components/Toast'

export default function EmployeeLayout() {
  const { user, clearAuth } = useAuthStore()
  const { success } = useToast()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  )
  const [collapsed, setCollapsed] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false)
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false)

  // Separate refs for mobile + desktop so click-outside works correctly on both
  const desktopDropdownRef = useRef<HTMLDivElement>(null)
  const mobileDropdownRef = useRef<HTMLDivElement>(null)

  const navGroups = [
    {
      label: 'Workspace',
      items: [
        { path: '/employee/dashboard', label: 'Home', icon: Home },
        { path: '/employee/history', label: 'History', icon: History },
      ],
    },
    {
      label: 'Actions',
      items: [
        { path: '/employee/eod-report', label: 'Report', icon: FileText },
        { path: '/employee/leave', label: 'Leave', icon: Briefcase },
      ],
    },
    {
      label: 'Info',
      items: [
        { path: '/employee/holidays', label: 'Holidays', icon: Award },
        { path: '/employee/profile', label: 'Profile', icon: User },
      ],
    },
  ]

  const navItems = navGroups.flatMap((g) => g.items)

  const closeAllDropdowns = () => {
    setIsDropdownOpen(false)
    setIsHeaderDropdownOpen(false)
    setIsMobileDropdownOpen(false)
  }

  const handleLogout = async () => {
    closeAllDropdowns()
    try {
      await authAPI.logout()
    } catch {
      // ignore
    } finally {
      success('Logged out successfully.')
      clearAuth()
      setTimeout(() => {
        window.location.href = '/login'
      }, 500)
    }
  }

  const isActive = (path: string) => location.pathname === path

  // Close desktop dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isHeaderDropdownOpen &&
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(e.target as Node)
      ) {
        setIsHeaderDropdownOpen(false)
      }
    }
    if (isHeaderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isHeaderDropdownOpen])

  // Close mobile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isMobileDropdownOpen &&
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(e.target as Node)
      ) {
        setIsMobileDropdownOpen(false)
      }
    }
    if (isMobileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobileDropdownOpen])

  // Close both dropdowns on route change
  useEffect(() => {
    setIsHeaderDropdownOpen(false)
    setIsMobileDropdownOpen(false)
  }, [location.pathname])

  return (
    <div className="flex h-screen bg-[#FAF6EE]">
      {/* ── Desktop sidebar ── */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-[#0F172A] text-white transition-all duration-300 md:relative md:translate-x-0 overflow-hidden flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'md:w-20 w-72' : 'w-72'
        )}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-[#F0821F]/20 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[#A31621]/20 blur-3xl" />
        </div>

        <div className="relative flex flex-col h-full">
          {/* Logo */}
          <div className={cn('border-b border-white/[0.06] flex-shrink-0', collapsed ? 'p-4' : 'p-6')}>
            <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-black/30 flex-shrink-0">
                <img
                  src="/logo.png"
                  alt="IRCS Haryana"
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                    const fallback = document.getElementById('employee-logo-fallback')
                    if (fallback) fallback.style.display = 'block'
                  }}
                />
                <Briefcase id="employee-logo-fallback" className="w-5 h-5 text-[#C24A0A]" style={{ display: 'none' }} />
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <h1 className="font-bold text-lg leading-tight">IRCS Haryana</h1>
                  <p className="text-xs text-white/50 leading-tight">Employee Portal</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-5 overflow-y-auto overflow-x-hidden">
            {navGroups.map((group, gi) => (
              <div key={group.label} className={gi > 0 ? 'mt-5' : ''}>
                {!collapsed && (
                  <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/30">
                    {group.label}
                  </p>
                )}
                {collapsed && gi > 0 && (
                  <div className="mx-auto mb-3 w-6 h-px bg-white/10" />
                )}
                <div className="space-y-1">
                  {group.items.map(({ path, label, icon: Icon }) => {
                    const active = isActive(path)
                    return (
                      <Link
                        key={path}
                        to={path}
                        title={collapsed ? label : undefined}
                        onClick={() => {
                          if (window.innerWidth < 768) setSidebarOpen(false)
                        }}
                        className={cn(
                          'group relative flex items-center rounded-xl transition-all duration-200 text-sm',
                          collapsed ? 'justify-center p-2.5' : 'gap-3 px-3.5 py-2.5',
                          active
                            ? 'bg-gradient-to-r from-[#F0821F] to-[#E4670F] text-white shadow-lg shadow-[#E4670F]/30 font-semibold'
                            : 'text-white/70 hover:text-white hover:bg-white/[0.06] font-medium'
                        )}
                      >
                        {active && !collapsed && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-white/80" />
                        )}
                        <Icon
                          className={cn(
                            'w-[18px] h-[18px] flex-shrink-0 transition-transform',
                            !active && 'group-hover:scale-110'
                          )}
                        />
                        {!collapsed && <span className="truncate">{label}</span>}

                        {collapsed && (
                          <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg bg-[#1E293B] text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all shadow-xl ring-1 ring-white/10 z-50">
                            {label}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className={cn('border-t border-white/[0.06] flex-shrink-0', collapsed ? 'p-3' : 'p-4')}>
            {collapsed ? (
              <div className="flex flex-col items-center gap-2">
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    title={user.full_name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[#F0821F]/40"
                  />
                ) : (
                  <div
                    title={user.full_name}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white/10"
                  >
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                  className="p-2 text-white/50 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-white/[0.05] border border-white/[0.06] rounded-xl">
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-[#F0821F]/40"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ring-2 ring-white/10">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-[11px] text-white/40 uppercase tracking-wide">Employee</p>
                  <p className="font-semibold text-white truncate text-sm leading-tight">{user?.full_name}</p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                  className="p-2 text-white/50 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden pb-20 md:pb-0">
        {/* ── Mobile top bar ── */}
        <header className="md:hidden bg-white/80 backdrop-blur-md border-b border-neutral-200/70 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-white rounded-xl ring-1 ring-neutral-200 flex items-center justify-center shadow-sm flex-shrink-0">
              <img
                src="/logo.png"
                alt="IRCS Haryana"
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                  const fallback = document.getElementById('mobile-logo-fallback')
                  if (fallback) fallback.style.display = 'block'
                }}
              />
              <Briefcase id="mobile-logo-fallback" className="w-5 h-5 text-[#C24A0A]" style={{ display: 'none' }} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-neutral-900 leading-tight text-sm truncate">{user?.full_name}</p>
              <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
            </div>
          </div>

          {/* Mobile dropdown — uses its OWN ref */}
          <div className="relative flex-shrink-0" ref={mobileDropdownRef}>
            <button
              type="button"
              onClick={() => setIsMobileDropdownOpen((v) => !v)}
              className={cn(
                'ml-2 p-2 rounded-lg transition-colors',
                isMobileDropdownOpen ? 'bg-neutral-100' : 'hover:bg-neutral-100'
              )}
              title="Menu"
              aria-label="Open profile menu"
              aria-expanded={isMobileDropdownOpen}
            >
              {isMobileDropdownOpen ? (
                <ChevronUp className="w-5 h-5 text-neutral-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-neutral-600" />
              )}
            </button>

            {isMobileDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl ring-1 ring-neutral-200/70 overflow-hidden z-50">
                {/* User header */}
                <div className="px-4 py-3 border-b border-neutral-100 bg-gradient-to-br from-neutral-50 to-white">
                  <div className="flex items-center gap-3">
                    {user?.photo ? (
                      <img
                        src={user.photo}
                        alt={user.full_name}
                        className="w-9 h-9 rounded-full object-cover ring-2 ring-[#F0821F]/30"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">
                        {user?.full_name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-neutral-900 truncate">{user?.full_name}</p>
                      <p className="text-[11px] text-neutral-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="p-1.5">
                  <Link
                    to="/employee/profile"
                    onClick={() => setIsMobileDropdownOpen(false)}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-[#E4670F]/[0.06] hover:text-[#C24A0A] transition-colors text-sm font-medium"
                  >
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-[#E4670F]/10 flex items-center justify-center transition-colors">
                      <User className="w-4 h-4" />
                    </div>
                    Profile
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors text-sm font-medium mt-0.5"
                  >
                    <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                      <LogOut className="w-4 h-4" />
                    </div>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* ── Desktop header ── */}
        <header className="hidden md:flex bg-white/80 backdrop-blur-md border-b border-neutral-200/70 px-6 py-3 items-center gap-3 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors text-neutral-600 hover:text-neutral-900"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-neutral-900 truncate">Employee Portal</h2>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* <div className="hidden lg:flex items-center gap-2 bg-neutral-100 rounded-lg px-3 py-1.5 text-sm text-neutral-400 w-56">
              <Search className="w-4 h-4" />
              <span className="text-xs">Search...</span>
            </div> */}
            <div className="hidden md:inline-flex items-center gap-1.5 bg-gradient-to-r from-[#F0821F]/10 to-[#E4670F]/10 border border-[#E4670F]/20 rounded-full px-3 py-1 text-[11px] font-semibold text-[#C24A0A]">
              <Sparkles className="w-3 h-3" />
              Active
            </div>

            {/* Desktop dropdown — uses its OWN ref */}
            <div className="relative" ref={desktopDropdownRef}>
              <button
                type="button"
                onClick={() => setIsHeaderDropdownOpen((v) => !v)}
                className={cn(
                  'flex items-center gap-2 p-1 rounded-full transition-all duration-200',
                  isHeaderDropdownOpen
                    ? 'ring-2 ring-[#E4670F]/40 bg-[#E4670F]/5'
                    : 'hover:ring-2 hover:ring-neutral-200'
                )}
                aria-label="Open profile menu"
                aria-expanded={isHeaderDropdownOpen}
              >
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[#F0821F]/30"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown
                  className={cn(
                    'hidden sm:block w-3.5 h-3.5 text-neutral-400 transition-transform duration-200',
                    isHeaderDropdownOpen && 'rotate-180'
                  )}
                />
              </button>

              {isHeaderDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl shadow-black/10 ring-1 ring-neutral-200/70 overflow-hidden">
                  <div className="px-4 py-3.5 border-b border-neutral-100 bg-gradient-to-br from-neutral-50 to-white">
                    <div className="flex items-center gap-3">
                      {user?.photo ? (
                        <img
                          src={user.photo}
                          alt={user.full_name}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-[#F0821F]/30"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white font-bold ring-2 ring-white">
                          {user?.full_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{user?.full_name}</p>
                        <p className="text-[11px] text-neutral-500 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <div className="mt-2.5">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Employee
                      </span>
                    </div>
                  </div>
                  <div className="p-1.5">
                    <Link
                      to="/employee/profile"
                      onClick={() => setIsHeaderDropdownOpen(false)}
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-[#E4670F]/[0.06] hover:text-[#C24A0A] transition-colors text-sm font-medium"
                    >
                      <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-[#E4670F]/10 flex items-center justify-center transition-colors">
                        <User className="w-4 h-4" />
                      </div>
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors text-sm font-medium mt-0.5"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                        <LogOut className="w-4 h-4" />
                      </div>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Mobile bottom navigation ── */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden bg-white/95 backdrop-blur-md border-t border-neutral-200/70 pb-[env(safe-area-inset-bottom)] z-40">
        <div className="flex items-stretch w-full">
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = isActive(path)
            return (
              <Link
                key={path}
                to={path}
                className={cn(
                  'relative flex flex-col items-center justify-center py-2.5 px-1 flex-1 min-w-0 transition-colors',
                  active ? 'text-[#C24A0A]' : 'text-neutral-500 hover:text-neutral-900'
                )}
              >
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-gradient-to-r from-[#F0821F] to-[#E4670F]" />
                )}
                <Icon className={cn('w-5 h-5 mb-1 flex-shrink-0 transition-transform', active && 'scale-110')} />
                <span className="text-[11px] leading-tight font-medium max-w-full truncate">{label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}